// Движок сцены слоёв: камера, пролёт вдоль ряда, угасание по глубине, выдвижение карточки,
// прореживание подписей. Перенесено из прототипа v14 (applyCam, arcTo, fitSide, applyScroll,
// thinLabels, setPull, applyLayerTF, pullTick). Работает по DOM напрямую, а не через
// реактивность: за кадр пролёта меняется сотня трансформаций и прозрачностей, и пересчёт
// шаблона на каждый кадр перерисовывал бы всю колоду.
//
// Договорённость с детьми сцены (LayerFlags, LayerEventMarks): любой элемент внутри .ly-track
// с атрибутами data-sz (глубина, px, отрицательная вглубь: −item.z) и data-kind
// ('layer' | 'rail' | 'isl' | 'isllab' | 'evt' | 'medarm' | 'flat' | 'flag' | 'flat2' | 'flag2')
// сцена ставит на место и гасит по глубине сама — трансформацию и прозрачность такому
// элементу задавать не нужно. Сдвиг первой карточки и прокрутку добавляет контейнер .ly-track.
// Подписи внутри такого элемента, помеченные data-lab, участвуют в прореживании наложений.

export const REDUCED = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const SCALE = 1.15;              // насколько «подъезжает» камера
export const PAD_L = 112;               // куда встаёт срез по горизонтали
export const TARGET = { x: -10, y: -36 };
// −66°, а не 90°: на прямом торце карточки схлопываются в нитки, лицо карточки ещё видно
export const SIDE = { x: -13, y: -66 };
export const PULL_X = 340, PULL_Z = 170, PULL_UP = 72, PART_X = 56, SPLIT_Z = 130, PULL_MS = 1150;

// дальность видимости в перспективе: полная до −820 px, ноль к −1340. Заказчик это
// проверяет — не менять.
export function depthOpacity(z) {
  if (z > 20) return 0;                                   // до среза не долетает
  if (z > -150) return Math.max(0, (20 - z) / 170);       // растворяется на подлёте
  if (z > -820) return 1;
  return Math.max(0, 1 - (-z - 820) / 520);               // растворяется вдали
}
// островки и разметка — карта, держатся дольше самих слоёв
function islOpacity(z) {
  if (z > 300) return 0;
  if (z > 80) return Math.max(0, 1 - (z - 80) / 220);
  if (z > -1600) return 1;
  return Math.max(0, 1 - (-z - 1600) / 700);
}

// доводчик: резкий старт и долгий мягкий доход; одна кривая на выезде и на возврате
const eOut = t => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
const seg = (k, a, b) => Math.max(0, Math.min(1, (k - a) / (b - a)));
const uOf = k => (k >= 1 ? 1 : -Math.log2(1 - Math.min(0.999, Math.max(0, k))) / 10);
const easeOut = t => 1 - Math.pow(1 - t, 3);

export default {
  created() {
    // Нереактивное состояние движка: меняется покадрово, шаблону не нужно.
    this.E = {
      x: 0, y: 0, z: 0, tx: 0, ty: 0, s: 1, camRAF: null,
      exploded: false, zOff: 0, xOff: 0,
      scrollEls: [], layerEls: new Map(),
      pullK: new Map(), pullRAF: null, pullTs: 0, pullSel: null, splitSel: null,
      wheelAcc: 0, wheelRAF: null, wheelOffT: null, flyRAF: null,
      thinT: null, thinT2: null, drag: null,
    };
  },
  beforeUnmount() {
    const E = this.E;
    [E.camRAF, E.pullRAF, E.wheelRAF, E.flyRAF].forEach(r => r && cancelAnimationFrame(r));
    clearTimeout(E.wheelOffT); clearTimeout(E.thinT); clearTimeout(E.thinT2);
  },
  methods: {
    // ---------- камера ----------
    applyCam() {
      const E = this.E, cam = this.$refs.cam;
      if (!cam) return;
      cam.style.transform = `translate3d(${E.tx}px, ${E.ty}px, ${E.z}px) `
        + `rotateX(${E.x}deg) rotateY(${E.y}deg) scale(${E.s})`;
      // подписи разворачиваем обратно, чтобы всегда смотрели в камеру
      cam.style.setProperty('--bx', (-E.x) + 'deg');
      cam.style.setProperty('--by', (-E.y) + 'deg');
      // Колонка флагов правее ряда. Сдвиг (G·cosφ, 0, G·sinφ·s) держит флаг на глубине его
      // карточки, но при развороте вбок колонка съезжает вдоль ряда; чистый (G, 0, 0) держит
      // строй, но синие расходятся по высоте. Смешиваем по углу: в лоб важнее первое, в торце
      // второе. scale() не трогает глубину, поэтому масштаб только в глубинной составляющей.
      const th = E.y * Math.PI / 180;
      const ay = Math.abs(E.y) % 360, y2 = ay > 180 ? 360 - ay : ay;
      const t01 = Math.max(0, Math.min(1, (y2 - 45) / 25));      // 45° — ещё в лоб, 70° — уже сбоку
      const q = 1 - t01 * t01 * (3 - 2 * t01);
      const mixX = g => g * (Math.cos(th) * q + (1 - q));
      const mixZ = g => g * Math.sin(th) * E.s * q;
      const gaps = this.flagGaps();
      cam.style.setProperty('--fdx', mixX(gaps[0]).toFixed(3) + 'px');
      cam.style.setProperty('--fdz', mixZ(gaps[0]).toFixed(3) + 'px');
      cam.style.setProperty('--gdx', mixX(gaps[1]).toFixed(3) + 'px');
      cam.style.setProperty('--gdz', mixZ(gaps[1]).toFixed(3) + 'px');
    },
    setCam(o) {
      Object.assign(this.E, { x: o.x, y: o.y, z: o.z, tx: o.tx, ty: o.ty, s: o.s });
      this.applyCam();
    },
    camState() {
      const E = this.E;
      return { x: E.x, y: E.y, z: E.z, tx: E.tx, ty: E.ty, s: E.s };
    },
    arcTo(target, dur, opt, done) {
      opt = opt || {};
      const E = this.E;
      if (E.camRAF) cancelAnimationFrame(E.camRAF);
      // после размонтирования (выход крошкой) отложенный полёт уже некуда показывать;
      // выход по Esc сам ставит exiting и летит — его пропускаем по done
      if (this.$refs.cam == null) return;
      if (REDUCED || dur <= 0) { this.setCam(target); if (done) done(); return; }
      const from = this.camState(), t0 = performance.now();
      const swing = opt.arc || 0, dolly = opt.dolly || 0;
      const lerp = (a, b, e) => a + (b - a) * e;
      const step = now => {
        const t = Math.min(1, (now - t0) / dur);
        const e = easeOut(t), s = Math.sin(Math.PI * t);
        this.setCam({
          x: lerp(from.x, target.x, e),
          y: lerp(from.y, target.y, e) + swing * s,
          z: lerp(from.z, target.z, e) - dolly * s,
          tx: lerp(from.tx, target.tx, e),
          ty: lerp(from.ty, target.ty, e),
          s: lerp(from.s, target.s, e),
        });
        if (t < 1) E.camRAF = requestAnimationFrame(step);
        else { E.camRAF = null; if (done) done(); }
      };
      E.camRAF = requestAnimationFrame(step);
    },
    // куда прилететь, чтобы срез встал слева, а веер ушёл вправо-вглубь
    focusTarget(rx, ry) {
      const g = this.geom;
      const cx = g.colLeft + g.colW / 2, cy = g.colTop + g.colH / 2;
      return { x: rx, y: ry, z: 0, s: SCALE,
        tx: (PAD_L + g.colW * SCALE / 2) - cx,
        ty: (this.padTop() + g.colH * SCALE / 2) - cy };
    },
    // Кадрируем по глубине ряда: с торца именно она занимает экран.
    sideTarget() {
      const g = this.geom;
      const cx = g.colLeft + g.colW / 2, cy = g.colTop + g.colH / 2;
      const depth = this.fullDepth() || 600;
      const st = this.$refs.stage;
      const w = (st && st.clientWidth) || 900, h = (st && st.clientHeight) || 620;
      const s2 = Math.max(0.40, Math.min(1, (w * 0.80) / Math.max(420, depth + 200)));
      return { x: SIDE.x, y: SIDE.y, z: 0, s: s2, tx: w * 0.11 - cx, ty: h * 0.53 - cy };
    },
    // Досадка по факту: при шаге «по датам» стопка шире расчёта. scale() глубину не жмёт,
    // поэтому отодвигаем камеру, пока колода не влезет, и только потом центрируем.
    fitSide() {
      if (!this.sideView) return;
      const stage = this.$refs.stage, cam = this.$refs.cam;
      if (!stage || !cam) return;
      const st = stage.getBoundingClientRect();
      // Меряем только видимое: карточки и плашки блока (П меряет все карточки, но у П пролёт
      // короче перспективы). После глубокого подлёта пролетевшие карточки стоят за камерой,
      // их рамки — мусор в десятки тысяч px, а плашки колонки блока без них уходили за край.
      const els = this.E.scrollEls.filter(it => it.vis && (it.kind === 'layer' || it.kind === 'flat' || it.kind === 'flag'))
        .map(it => it.el);
      const measure = () => {
        let l = Infinity, r = -Infinity;
        els.forEach(e => {
          const b = e.getBoundingClientRect();
          if (b.width) { l = Math.min(l, b.left); r = Math.max(r, b.right); }
        });
        return { l, r, w: r - l, c: (l + r) / 2 };
      };
      const need = st.width * 0.94;
      for (let n = 0; n < 10; n++) {
        const m = measure();
        if (!isFinite(m.l) || m.w <= 0 || m.w <= need) break;
        const cur = this.camState();
        this.setCam({ ...cur, z: cur.z - Math.max(200, Math.min(1600, (m.w - need) * 1.2)) });
      }
      const m2 = measure();
      if (isFinite(m2.l)) {
        const cur = this.camState();
        this.setCam({ ...cur, tx: cur.tx + (st.left + st.width / 2 - m2.c) });
      }
    },
    // В торце досаживаем и после перекадрирования: оно перебивает полёт toSide вместе с его
    // fitSide (смена блока, пересборка), и колонка плашек оставалась за левым краем.
    reframe(dur) {
      // выход уже летит своей дугой: перекадрирование (конец пролёта, пересборка) сбило бы её
      // вместе с $emit('exit'), и Esc после входа с клетки оставлял слои открытыми
      if (this.exiting) return;
      this.arcTo(this.sideView ? this.sideTarget() : this.focusTarget(this.E.x, this.E.y),
        dur === undefined ? 520 : dur, undefined, this.sideView ? () => this.fitSide() : undefined);
    },

    // ---------- подвижные элементы ----------
    // Список кешируется при каждой перестройке DOM: querySelectorAll на каждый кадр
    // прокрутки при сотне слоёв даёт заметные проседания.
    collectScrollEls() {
      const cam = this.$refs.cam;
      if (!cam) return;
      const E = this.E;
      E.scrollEls = Array.from(cam.querySelectorAll('[data-sz]')).map(el => ({
        el, kind: el.dataset.kind, base: Number(el.dataset.sz),
        kx: Number(el.dataset.kx || 0), ky: Number(el.dataset.ky || 0),
        baseop: Number(el.dataset.baseop || 1),
        // строй и коромысло (data-an) — сводка по заголовкам, а не карточки ряда: колонка
        // едет вместе с камерой, иначе после подлёта к блоку она остаётся за срезом
        an: el.dataset.an !== undefined,
        labs: el.classList.contains('ly-unit')
          ? Array.from(el.querySelectorAll('.ly-date,.ly-gap,.ly-tag,.ly-phcap'))
          : el.hasAttribute('data-lab') ? [el]
          : el.querySelector('[data-lab]') ? Array.from(el.querySelectorAll('[data-lab]')) : null,
        // узлы переживают перестройку (Vue правит, а не создаёт заново), скрытость берём с узла
        vis: el.style.visibility !== 'hidden', op: -1,
      }));
      E.layerEls = new Map();
      E.scrollEls.forEach(it => {
        if (it.kind === 'layer') E.layerEls.set(Number(it.el.dataset.layer), it);
      });
    },
    trackTransform() {
      const E = this.E, tr = this.$refs.track;
      if (!tr) return;
      tr.style.transform = E.exploded
        ? `translate3d(${E.xOff}px, 0px, ${this.scrollZ - E.zOff}px)` : 'translate3d(0px, 0px, 0px)';
    },
    // ставим элементы на места один раз; дальше при прокрутке едет только контейнер
    placeStatic() {
      const E = this.E;
      const bill = ' rotateY(var(--by,0deg)) rotateX(var(--bx,0deg))';
      const center = this.pullCenter();
      E.scrollEls.forEach(it => {
        const s = it.el.style;
        if (it.kind === 'layer') {
          it.el.classList.toggle('ly-pull', E.pullSel !== null && Number(it.el.dataset.layer) === E.pullSel);
          this.applyLayerTF(it, center);
        }
        else if (it.kind === 'rail') s.transform = 'rotateX(-90deg)';
        else if (it.kind === 'medarm')
          s.transform = `translateZ(${it.base}px) rotateY(${it.el.dataset.dir === '1' ? -90 : 90}deg)`;
        else if (it.kind === 'isl') s.transform = `translateZ(${it.base}px) rotateX(-90deg)`;
        else if (/^fla[tg]2?$/.test(it.kind)) this.placeFlag(it);
        else if (it.kind === 'isllab' || it.kind === 'evt') s.transform = `translateZ(${it.base}px)` + bill;
      });
      this.sliceFocus();
    },
    // Древко живёт в плоскости карточек: только так оно остаётся параллельным ряду;
    // полотнище — билборд. Строй и коромысло компенсируют прокрутку контейнера.
    placeFlag(it) {
      const v = it.kind === 'flag' || it.kind === 'flat' ? 'fd' : 'gd';
      const z = it.an && this.E.exploded ? it.base - this.scrollZ : it.base;
      it.el.style.transform = `translate3d(var(--${v}x,0px), 0px, calc(${z}px + var(--${v}z,0px)))`
        + (it.kind.startsWith('flag') ? ' rotateY(var(--by,0deg)) rotateX(var(--bx,0deg))'
          : this.sideView ? ' rotateY(var(--by,0deg))' : '');
    },
    // выдвинута карточка или раздвинута пара — срез приглушаем, подписи блоков меньше
    sliceFocus() {
      const E = this.E, sl = this.$refs.slice;
      if (!sl) return;
      const focusOn = E.pullSel !== null || E.splitSel !== null;
      Array.from(sl.children).forEach(ch => {
        ch.style.opacity = !focusOn ? '' : (ch.classList.contains('ly-rl') ? '0.55' : '0.1');
      });
    },
    explode() {
      this.E.exploded = true;
      this.placeStatic();
      this.applyScroll();
      this.shiftLabels(true);
      this.scheduleThin();
    },
    shiftLabels(on) {
      const sl = this.$refs.slice;
      if (!sl) return;
      sl.querySelectorAll('.ly-rl').forEach(el => {
        el.style.transform = `translateX(${on ? el.dataset.shift : 0}px)`;
      });
    },
    collapse() {
      const E = this.E;
      E.exploded = false; E.pullSel = null; E.splitSel = null; E.pullK.clear();
      this.trackTransform();
      const list = E.scrollEls.filter(it => it.kind === 'layer');
      list.forEach((it, i) => {
        it.el.style.transitionDelay = REDUCED ? '0ms' : Math.min(400, (list.length - i) * 24) + 'ms';
        it.el.style.transform = 'translateZ(0px)';
        it.el.style.opacity = it.el.dataset.baseop || '1';
        it.el.style.visibility = '';
      });
      E.scrollEls.forEach(it => { if (it.kind !== 'layer' && it.kind !== 'rail') it.el.style.opacity = '0'; });
      this.shiftLabels(false);
    },

    // ---------- пролёт ----------
    applyScroll() {
      const E = this.E;
      this.trackTransform();
      const sc = E.exploded ? this.scrollZ : 0;
      const off = E.exploded ? -E.zOff : 0;
      const focusOn = E.pullSel !== null || E.splitSel !== null;
      const band = this.medBand;
      E.scrollEls.forEach(it => {
        if (it.kind === 'rail') return;
        if (it.an) this.placeFlag(it);
        const z = it.base + (it.an ? 0 : sc) + off;
        let op;
        if (it.kind === 'layer') {
          const li = Number(it.el.dataset.layer);
          const pull = E.pullSel !== null && li === E.pullSel;
          // пара вокруг разрыва остаётся в полную силу — по ней читается, между кем событие
          const inPair = E.splitSel !== null && (li === E.splitSel[0] || li === E.splitSel[1]);
          op = (pull || inPair) ? (pull ? 1 : depthOpacity(z))
            : depthOpacity(z) * Math.min(it.baseop, focusOn ? 0.14 : 1);
          // выбрана группа медианы — остальное приглушаем, но не гасим
          if (band && (li < band[0] || li > band[1])) op *= 0.42;
        }
        else if (it.kind === 'isl') op = 0.4 * islOpacity(z) * (focusOn ? 0.3 : 1);
        else {
          const keep = it.el.dataset.ev !== undefined && Number(it.el.dataset.ev) === this.evSel;
          op = islOpacity(z) * (focusOn && !keep ? 0.3 : 1);
        }
        // разметка медианы — прибор, держим нижний порог, иначе её не видно у дальнего края
        if (it.el.dataset.medinstr) op = Math.max(op, 0.5);
        if (op < 0.05) {
          if (it.vis) { it.el.style.visibility = 'hidden'; it.vis = false; }
          return;
        }
        if (!it.vis) { it.el.style.visibility = ''; it.vis = true; }
        const r = Math.round(op * 50) / 50;
        if (r !== it.op) { it.el.style.opacity = String(r); it.op = r; }
      });
      this.onScrolled();
    },
    scrollBy(dz) {
      const E = this.E;
      if (!E.exploded || this.maxScroll < 40) return false;
      if ((dz > 0 && this.scrollZ >= this.maxScroll) || (dz < 0 && this.scrollZ <= 0)) return false;
      if (!this.flying) this.flying = true;      // переходы съедают кадр
      clearTimeout(E.wheelOffT);
      E.wheelOffT = setTimeout(() => { this.flying = false; this.thinLabels(); }, 160);
      E.wheelAcc += dz;                          // дельту копим и применяем раз за кадр
      if (!E.wheelRAF) E.wheelRAF = requestAnimationFrame(() => {
        E.wheelRAF = null;
        const next = Math.round(Math.max(0, Math.min(this.maxScroll, this.scrollZ + E.wheelAcc)));
        E.wheelAcc = 0;
        if (next === this.scrollZ) return;
        this.scrollZ = next;
        this.applyScroll();
      });
      return true;
    },
    onWheel(e) {
      if (this.maxScroll < 40 || !this.inScene(e.target)) return;
      // на краях диапазона скролл отдаём странице, чтобы не запирать её
      if (this.scrollBy((e.deltaY + e.deltaX * 0.4) * 1.9)) e.preventDefault();
    },
    // плавный пролёт: само движение показывает, сколько сессий осталось позади
    flyTo(target, dur, then) {
      const E = this.E;
      if (this.exiting) return;
      target = Math.round(Math.max(0, Math.min(this.maxScroll, target)));
      const start = this.scrollZ, path = target - start, t0 = performance.now();
      if (E.flyRAF) cancelAnimationFrame(E.flyRAF);
      if (REDUCED || Math.abs(path) < 12 || this.maxScroll < 40) {
        this.scrollZ = target; this.applyScroll(); this.scheduleThin();
        if (then) then();
        return;
      }
      const step = () => {
        const k = Math.min(1, (performance.now() - t0) / dur);
        // разгон и торможение: у постоянной скорости конец выглядит как обрыв
        const e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
        this.scrollZ = Math.round(start + path * e);
        this.applyScroll();
        if (k < 1) E.flyRAF = requestAnimationFrame(step);
        else { E.flyRAF = null; this.scheduleThin(); this.reframe(420); if (then) then(); }
      };
      E.flyRAF = requestAnimationFrame(step);
    },

    // ---------- вращение мышью ----------
    // вращаем только за саму сцену: панели поверх неё (Ф6, разбор) живут своими кликами
    inScene(t) { return t === this.$refs.stage || (this.$refs.cam && this.$refs.cam.contains(t)); },
    onDown(e) {
      if (e.button !== 0 || !this.inScene(e.target) || e.target.closest('.ly-t, button')) return;
      if (this.E.camRAF) cancelAnimationFrame(this.E.camRAF);
      this.E.drag = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    },
    onMove(e) {
      const E = this.E;
      if (!E.drag) return;
      E.y += (e.clientX - E.drag.x) * 0.4;
      E.x -= (e.clientY - E.drag.y) * 0.4;
      E.x = Math.max(-85, Math.min(85, E.x));
      // в торце оставляем люфт: чуть покрутить можно, уехать — нет
      if (this.sideView) {
        E.y = Math.max(SIDE.y - 22, Math.min(SIDE.y + 22, E.y));
        E.x = Math.max(SIDE.x - 16, Math.min(SIDE.x + 16, E.x));
      }
      E.drag = { x: e.clientX, y: e.clientY };
      this.applyCam();
    },
    onUp() { this.E.drag = null; },

    // ---------- выдвижение карточки ----------
    pullCenter() {
      let ci = null, ck = 0;
      this.E.pullK.forEach((e, i) => { if (e.mode === 'pull' && e.k > ck) { ck = e.k; ci = i; } });
      return [ci, ck];
    },
    // Два такта одного движения: карточка приподнимается над рядом, затем идёт вперёд
    // и вправо и на подходе опускается обратно.
    applyLayerTF(it, center) {
      const li = Number(it.el.dataset.layer);
      const ent = this.E.pullK.get(li);
      const k = ent ? ent.k : 0;
      let dx = 0, dy = 0, dz = 0;
      if (k > 0.0008 && ent && ent.mode !== 'pull') {
        // пара вокруг разрыва разъезжается по глубине, соседи — слабее
        const f = eOut(seg(uOf(k), 0, 0.62)) * ent.w;
        dz = (ent.mode === 'spA' ? SPLIT_Z : -SPLIT_Z) * f;
      } else if (k > 0.0008) {
        // такты режем по реальному времени u, а не по k: у доводчика начало слишком резкое
        const u = uOf(k);
        const fwd = eOut(seg(u, 0.13, 1));
        const up = Math.sin(Math.min(seg(u, 0.02, 0.26), 1 - seg(u, 0.50, 0.95)) * Math.PI / 2);
        dx = PULL_X * fwd; dy = -PULL_UP * up; dz = PULL_Z * fwd;
      }
      const c = center || this.pullCenter();
      if (c[0] !== null && li !== c[0]) {
        const d = li - c[0], ad = Math.abs(d);
        // коридор открывается за первые 20 % времени — раньше, чем карточка стронется
        if (ad <= 3) dx += Math.sign(d) * PART_X * (1 - (ad - 1) / 3.4) * seg(uOf(c[1]), 0, 0.20);
      }
      // в торце карточку доворачиваем лицом к камере вокруг её собственного центра
      it.el.style.transform = `translate3d(${it.kx + dx}px, ${it.ky + dy}px, ${it.base + dz}px)`
        + (this.sideView ? ' rotateY(var(--by,0deg))' : '');
    },
    pullTouched() {
      const E = this.E, set = new Set();
      const around = ci => { set.add(ci); for (let d = -3; d <= 3; d++) if (E.layerEls.has(ci + d)) set.add(ci + d); };
      E.pullK.forEach((e, i) => around(i));
      if (E.pullSel !== null) around(E.pullSel);
      return set;
    },
    applyPull(touched) {
      const center = this.pullCenter();
      (touched || this.pullTouched()).forEach(i => {
        const it = this.E.layerEls.get(i);
        if (it) this.applyLayerTF(it, center);
      });
    },
    pullTick(ts) {
      const E = this.E;
      E.pullRAF = null;
      const dt = E.pullTs ? Math.min(64, ts - E.pullTs) : 16;
      E.pullTs = ts;
      // участников берём до правки: на последнем кадре запись удаляется
      const touched = this.pullTouched();
      let alive = false;
      E.pullK.forEach((e, i) => {
        e.t = Math.min(1, e.t + dt / PULL_MS);
        e.k = e.k0 + (e.to - e.k0) * eOut(e.t);
        if (e.t >= 1) { e.k = e.to; if (e.to <= 0) E.pullK.delete(i); }
        else alive = true;
      });
      this.applyPull(touched);
      if (alive) E.pullRAF = requestAnimationFrame(t => this.pullTick(t));
      else {
        E.pullTs = 0;
        E.layerEls.forEach(it => { it.el.style.transition = ''; });
      }
    },
    // Выдвинуть слой i и/или раздвинуть пару split=[a,b]; группа медианы раздвигает ряд
    // с обеих сторон. Все движения живут в одном цикле.
    setPull(i, split) {
      const E = this.E;
      if (this.exiting && i !== null) return;
      E.pullSel = i;
      E.splitSel = split || null;
      if (!E.exploded) { E.pullK.clear(); return; }
      E.layerEls.forEach((it, j) => it.el.classList.toggle('ly-pull', j === i));
      const targets = new Map();
      if (i !== null) targets.set(i, { mode: 'pull', w: 1 });
      const spread = (a0, b0) => {
        for (let d = 0; d <= 2; d++) {
          const w = 1 - d / 3;
          if (E.layerEls.has(a0 - d) && !targets.has(a0 - d)) targets.set(a0 - d, { mode: 'spA', w });
          if (E.layerEls.has(b0 + d) && !targets.has(b0 + d)) targets.set(b0 + d, { mode: 'spB', w });
        }
      };
      if (E.splitSel) spread(E.splitSel[0], E.splitSel[1]);
      if (this.medBand) spread(this.medBand[0] - 1, this.medBand[1] + 1);
      targets.forEach((t, j) => {
        const e = E.pullK.get(j);
        if (!e) E.pullK.set(j, { k: 0, k0: 0, to: 1, t: 0, mode: t.mode, w: t.w });
        else if (e.mode !== t.mode || e.w !== t.w || e.to !== 1) {
          e.k0 = e.k; e.to = 1; e.t = 0; e.mode = t.mode; e.w = t.w;
        }
      });
      E.pullK.forEach((e, j) => { if (!targets.has(j) && e.to !== 0) { e.k0 = e.k; e.to = 0; e.t = 0; } });
      // пока карточка едет, трансформ задаём покадрово — CSS-переход тут только мешал бы
      this.pullTouched().forEach(j => {
        const it = E.layerEls.get(j);
        if (it) it.el.style.transition = 'opacity .34s ease';
      });
      if (!E.pullRAF) { E.pullTs = 0; E.pullRAF = requestAnimationFrame(t => this.pullTick(t)); }
      this.sliceFocus();
      this.applyScroll();
      this.scheduleThin();
    },

    // ---------- прореживание подписей ----------
    // Перспектива сводит дальние слои в точку, заранее раскладкой это не посчитать:
    // после расстановки подпись сперва сжимаем, и только если и так не влезла — прячем.
    thinLabels() {
      const E = this.E;
      const groups = [];
      E.scrollEls.forEach(it => {
        if (!it.labs || !it.labs.length) return;
        if (!it.vis || it.op <= 0.25) {
          it.labs.forEach(e => { if (e.style.visibility !== 'hidden') e.style.visibility = 'hidden'; });
          return;
        }
        groups.push({ z: it.base, labs: it.labs, ev: it.kind === 'evt',
          med: it.el.dataset.medinstr === '1',
          sel: it.el.dataset.ev !== undefined && Number(it.el.dataset.ev) === this.evSel });
      });
      // разметка медианы первой: её включили осознанно; дальше раскрытое событие, события, даты
      groups.sort((a, b) => (b.med - a.med) || (b.sel - a.sel) || (b.ev - a.ev) || b.z - a.z);
      const flat = [], flatMed = [];
      groups.forEach(g => g.labs.forEach(e => {
        if (e.style.visibility === 'hidden') e.style.visibility = '';
        e.classList.remove('ly-mini', 'ly-dotonly');
        const ev = e.closest('.ly-ev3');
        if (ev) ev.classList.remove('ly-hushed');
        flat.push(e); flatMed.push(g.med);
      }));
      const clashIn = (r, kept) => kept.some(k => {
        const ox = Math.min(k.right, r.right) - Math.max(k.left, r.left);
        if (ox <= 0) return false;
        const oy = Math.min(k.bottom, r.bottom) - Math.max(k.top, r.top);
        return oy > 0 && (ox * oy) / (r.width * r.height) > 0.1;
      });
      const rects = flat.map(e => e.getBoundingClientRect());   // один лейаут на всё
      const kept = [], squeeze = [];
      flat.forEach((e, n) => {
        const r = rects[n];
        if (!r.width || !r.height) return;
        if (flatMed[n]) {
          kept.push({ left: r.left - 8, right: r.right + 8, top: r.top - 6, bottom: r.bottom + 6 });
          return;
        }
        if (clashIn(r, kept)) squeeze.push(n); else kept.push(r);
      });
      const stage = (list, cls) => {
        const rest = [];
        list.forEach(n => flat[n].classList.add(cls));
        const rs = list.map(n => flat[n].getBoundingClientRect());
        list.forEach((n, j) => {
          const r = rs[j];
          if (!r.width || !r.height) return;
          if (clashIn(r, kept)) rest.push(n); else kept.push(r);
        });
        return rest;
      };
      const mute = stage(stage(squeeze, 'ly-mini'), 'ly-dotonly');
      // не влез даже номер — прячем и саму отметку: линия без единого знака хуже её отсутствия
      mute.forEach(n => {
        flat[n].style.visibility = 'hidden';
        const ev = flat[n].closest('.ly-ev3');
        if (ev) ev.classList.add('ly-hushed');
      });
    },
    // первый проход сразу, второй — когда слои долетели (разлёт длится почти секунду)
    scheduleThin() {
      const E = this.E;
      clearTimeout(E.thinT); E.thinT = setTimeout(() => this.thinLabels(), 90);
      clearTimeout(E.thinT2); E.thinT2 = setTimeout(() => this.thinLabels(), REDUCED ? 140 : 1080);
    },
  },
};
